import { useAiProviderKeys } from "@/hooks/use-ai-provider-keys";
import { useState, useEffect } from "react";
import { ProvidersTable } from "./setting/ProvidersTable";
import { ProviderConfigDialog } from "./setting/ProviderConfigDialog";
import { OllamaConfigDialog } from "./setting/OllamaConfigDialog";
import { LiteLLMConfigDialog } from "./setting/LiteLLMConfigDialog";
import { OpenRouterConfigDialog } from "./setting/OpenRouterConfigDialog";
import { ServerApiKeySection } from "./setting/ServerApiKeySection";
import { AccountProfileSection } from "./setting/AccountProfileSection";
import { WorkspaceSettingsSection } from "./app-workspace/WorkspaceSettingsSection";
import { MembersSection } from "./app-workspace/MembersSection";
import { BillingSection } from "./app-workspace/BillingSection";
import { MCPServerSettingsSection } from "./setting/MCPServerSettingsSection";
import { useAppWorkspace } from "@/contexts/AppWorkspaceContext";
import { SettingsSideNav } from "./setting/SettingsSideNav";
import { ServerWithName } from "@/hooks/use-app-state";
import { ServerFormData } from "@/shared/types.js";

interface ProviderConfig {
  id: string;
  name: string;
  logo: string;
  logoAlt: string;
  description: string;
  placeholder: string;
  getApiKeyUrl: string;
}

interface SettingsTabProps {
  // Props for ServersTab
  connectedServerConfigs?: Record<string, ServerWithName>;
  onConnect?: (formData: ServerFormData) => void;
  onDisconnect?: (serverName: string) => void;
  onReconnect?: (serverName: string) => void;
  onUpdate?: (
    originalServerName: string,
    formData: ServerFormData,
    skipAutoConnect?: boolean,
  ) => void;
  onRemove?: (serverName: string) => void;
  // Initial section to display (e.g., "mcp-server" from URL hash #settings/mcp-server)
  initialSection?: string;
}

export function SettingsTab({
  connectedServerConfigs = {},
  onConnect = () => {},
  onDisconnect = () => {},
  onReconnect = () => {},
  onUpdate = () => {},
  onRemove = () => {},
  initialSection,
}: SettingsTabProps = {}) {
  const {
    tokens,
    setToken,
    clearToken,
    hasToken,
    getOllamaBaseUrl,
    setOllamaBaseUrl,
    getLiteLLMBaseUrl,
    setLiteLLMBaseUrl,
    getLiteLLMModelAlias,
    setLiteLLMModelAlias,
    getOpenRouterSelectedModels,
    setOpenRouterSelectedModels,
  } = useAiProviderKeys();

  // Valid section IDs for settings
  const validSections = [
    "profile",
    "api-keys",
    "general",
    "mcp-server",
    "members",
    "billing",
  ];

  // Normalize section - return "general" if invalid
  const normalizeSection = (section: string | undefined): string => {
    if (!section) return "general";
    return validSections.includes(section) ? section : "general";
  };

  const [activeSection, setActiveSection] = useState(
    normalizeSection(initialSection),
  );

  // Update active section when initialSection prop changes
  useEffect(() => {
    if (initialSection) {
      setActiveSection(normalizeSection(initialSection));
    }
  }, [initialSection]);
  const [editingValue, setEditingValue] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] =
    useState<ProviderConfig | null>(null);
  const [ollamaDialogOpen, setOllamaDialogOpen] = useState(false);
  const [ollamaUrl, setOllamaUrl] = useState("");
  const [litellmDialogOpen, setLitellmDialogOpen] = useState(false);
  const [litellmUrl, setLitellmUrl] = useState("");
  const [litellmApiKey, setLitellmApiKey] = useState("");
  const [litellmModelAlias, setLitellmModelAlias] = useState("");
  const [openRouterDialogOpen, setOpenRouterDialogOpen] = useState(false);
  const [openRouterApiKeyInput, setOpenRouterApiKeyInput] = useState("");
  const [openRouterSelectedModelsInput, setOpenRouterSelectedModelsInput] =
    useState<string[]>([]);

  const providerConfigs: ProviderConfig[] = [
    {
      id: "openai",
      name: "OpenAI",
      logo: "/openai_logo.png",
      logoAlt: "OpenAI",
      description: "GPT-4, GPT-4o, GPT-4o-mini, GPT-4.1, GPT-5, etc.",
      placeholder: "sk-...",
      getApiKeyUrl: "https://platform.openai.com/api-keys",
    },
    {
      id: "anthropic",
      name: "Anthropic",
      logo: "/claude_logo.png",
      logoAlt: "Claude",
      description: "Claude 3.5, Claude 3.7, Claude Opus 4, etc.",
      placeholder: "sk-ant-...",
      getApiKeyUrl: "https://console.anthropic.com/",
    },
    {
      id: "deepseek",
      name: "DeepSeek",
      logo: "/deepseek_logo.svg",
      logoAlt: "DeepSeek",
      description: "DeepSeek Chat, DeepSeek Reasoner, etc.",
      placeholder: "sk-...",
      getApiKeyUrl: "https://platform.deepseek.com/api_keys",
    },
    {
      id: "google",
      name: "Google AI",
      logo: "/google_logo.png",
      logoAlt: "Google AI",
      description: "Gemini 2.5, Gemini 2.5 Flash, Gemini 2.5 Flash Lite",
      placeholder: "AI...",
      getApiKeyUrl: "https://aistudio.google.com/app/apikey",
    },
    {
      id: "mistral",
      name: "Mistral AI",
      logo: "/mistral_logo.png",
      logoAlt: "Mistral AI",
      description: "Mistral Large, Mistral Small, Codestral, etc.",
      placeholder: "...",
      getApiKeyUrl: "https://console.mistral.ai/api-keys/",
    },
    {
      id: "xai",
      name: "xAI",
      logo: "/xai_logo.png",
      logoAlt: "xAI Grok",
      description: "Grok 3, Grok 3 Mini, Grok Code Fast 1, etc.",
      placeholder: "xai-...",
      getApiKeyUrl: "https://console.x.ai/",
    },
  ];

  const handleEdit = (providerId: string) => {
    const provider = providerConfigs.find((p) => p.id === providerId);
    if (provider) {
      setSelectedProvider(provider);
      const tokenValue = tokens[providerId as keyof typeof tokens];
      setEditingValue(
        Array.isArray(tokenValue) ? tokenValue.join(", ") : tokenValue || "",
      );
      setDialogOpen(true);
    }
  };

  const handleSave = () => {
    if (selectedProvider) {
      setToken(selectedProvider.id as keyof typeof tokens, editingValue);
      setDialogOpen(false);
      setSelectedProvider(null);
      setEditingValue("");
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setSelectedProvider(null);
    setEditingValue("");
  };

  const handleDelete = (providerId: string) => {
    clearToken(providerId as keyof typeof tokens);
    // Also clear OpenRouter selected models if deleting OpenRouter provider
    if (providerId === "openrouter") {
      setOpenRouterSelectedModels([]);
    }
  };

  const handleOllamaEdit = () => {
    setOllamaUrl(getOllamaBaseUrl());
    setOllamaDialogOpen(true);
  };

  const handleOllamaSave = () => {
    setOllamaBaseUrl(ollamaUrl);
    setOllamaDialogOpen(false);
    setOllamaUrl("");
  };

  const handleOllamaCancel = () => {
    setOllamaDialogOpen(false);
    setOllamaUrl("");
  };

  const handleLiteLLMEdit = () => {
    setLitellmUrl(getLiteLLMBaseUrl());
    setLitellmApiKey(tokens.litellm || "");
    setLitellmModelAlias(getLiteLLMModelAlias());
    setLitellmDialogOpen(true);
  };

  const handleLiteLLMSave = () => {
    setLiteLLMBaseUrl(litellmUrl);
    setToken("litellm", litellmApiKey);
    setLiteLLMModelAlias(litellmModelAlias);
    setLitellmDialogOpen(false);
    setLitellmUrl("");
    setLitellmApiKey("");
    setLitellmModelAlias("");
  };

  const handleLiteLLMCancel = () => {
    setLitellmDialogOpen(false);
    setLitellmUrl("");
    setLitellmApiKey("");
    setLitellmModelAlias("");
  };

  const handleOpenRouterEdit = () => {
    const currentModels = getOpenRouterSelectedModels();
    setOpenRouterApiKeyInput(tokens.openrouter || "");
    setOpenRouterSelectedModelsInput(currentModels);
    setOpenRouterDialogOpen(true);
  };

  const handleOpenRouterSave = (apiKey: string, selectedModels: string[]) => {
    setToken("openrouter", apiKey);
    setOpenRouterSelectedModels(selectedModels);
    setOpenRouterDialogOpen(false);
  };

  const handleOpenRouterModelsChange = (models: string[]) => {
    setOpenRouterSelectedModelsInput(models);
  };

  const handleOpenRouterCancel = () => {
    setOpenRouterDialogOpen(false);
    setOpenRouterApiKeyInput("");
    setOpenRouterSelectedModelsInput([]);
  };

  const { userRole } = useAppWorkspace();

  const renderContent = () => {
    switch (activeSection) {
      case "profile":
        return <AccountProfileSection />;
      case "api-keys":
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-2xl font-bold mb-6">API Keys</h1>
            </div>

            <ServerApiKeySection />

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">LLM Provider API Keys</h3>
              </div>

              <ProvidersTable
                providerConfigs={providerConfigs}
                hasToken={(providerId) =>
                  hasToken(providerId as keyof typeof tokens)
                }
                onEditProvider={handleEdit}
                onDeleteProvider={handleDelete}
                ollamaBaseUrl={getOllamaBaseUrl()}
                onEditOllama={handleOllamaEdit}
                litellmBaseUrl={getLiteLLMBaseUrl()}
                litellmModelAlias={getLiteLLMModelAlias()}
                onEditLiteLLM={handleLiteLLMEdit}
                openRouterSelectedModels={getOpenRouterSelectedModels()}
                onEditOpenRouter={handleOpenRouterEdit}
              />
            </div>
          </div>
        );
      case "general":
        return <WorkspaceSettingsSection />;
      case "mcp-server":
        return (
          <MCPServerSettingsSection
            connectedServerConfigs={connectedServerConfigs}
            onConnect={onConnect}
            onDisconnect={onDisconnect}
            onReconnect={onReconnect}
            onUpdate={onUpdate}
            onRemove={onRemove}
          />
        );
      case "members":
        return <MembersSection />;
      case "billing":
        return <BillingSection />;
      default:
        // Default to general for any invalid/unknown section
        return <WorkspaceSettingsSection />;
    }
  };

  return (
    <div className="h-full flex">
      <SettingsSideNav
        activeSection={activeSection}
        onSectionChange={setActiveSection}
      />
      <div className="flex-1 overflow-y-auto">
        <div className="container mx-auto p-6 max-w-4xl">{renderContent()}</div>

        {/* API Key Configuration Dialog */}
        <ProviderConfigDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          provider={selectedProvider}
          value={editingValue}
          onValueChange={setEditingValue}
          onSave={handleSave}
          onCancel={handleCancel}
        />

        {/* Ollama URL Configuration Dialog */}
        <OllamaConfigDialog
          open={ollamaDialogOpen}
          onOpenChange={setOllamaDialogOpen}
          value={ollamaUrl}
          onValueChange={setOllamaUrl}
          onSave={handleOllamaSave}
          onCancel={handleOllamaCancel}
        />

        {/* LiteLLM Configuration Dialog */}
        <LiteLLMConfigDialog
          open={litellmDialogOpen}
          onOpenChange={setLitellmDialogOpen}
          baseUrl={litellmUrl}
          apiKey={litellmApiKey}
          modelAlias={litellmModelAlias}
          onBaseUrlChange={setLitellmUrl}
          onApiKeyChange={setLitellmApiKey}
          onModelAliasChange={setLitellmModelAlias}
          onSave={handleLiteLLMSave}
          onCancel={handleLiteLLMCancel}
        />

        {/* OpenRouter Configuration Dialog */}
        <OpenRouterConfigDialog
          open={openRouterDialogOpen}
          onOpenChange={setOpenRouterDialogOpen}
          apiKey={openRouterApiKeyInput}
          selectedModels={openRouterSelectedModelsInput}
          onApiKeyChange={setOpenRouterApiKeyInput}
          onSelectedModelsChange={handleOpenRouterModelsChange}
          onSave={handleOpenRouterSave}
          onCancel={handleOpenRouterCancel}
        />
      </div>
    </div>
  );
}
